import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useMagicLinkVerification from '../../../access/useMagicLinkVerification';
import {
  createFormFlowApi,
  requestFlowMagicLink,
  verifyFlowMagicLink,
} from './formFlowApiClient';

const DRAFT_DEBOUNCE_MS = 500;
const SSE_FALLBACK_MS = 3000;
const EMPTY_VALIDATION = Object.freeze({ errors: {}, warnings: {} });

/**
 * Map a 401/403 error body from {@code FormFlowController} to a gate-state string that the
 * shell consumes. The body is produced by {@code FlowAuthRequiredException} or
 * {@code FlowAccessDeniedException}, which serialise {@code accessMode}, {@code componentId}
 * and {@code formSessionId}.
 */
function deriveGateState(status, accessMode) {
  if (status === 403) {
    if (accessMode === 'DISABLED') return 'disabled';
    return 'access_denied';
  }
  if (status === 401) {
    if (accessMode === 'EMAIL_MAGIC_LINK') return 'email_required';
    if (typeof accessMode === 'string' && accessMode.startsWith('AUTHENTICATED')) {
      return 'auth_required';
    }
  }
  return null;
}

/**
 * Normalise a server validation payload into the shape {@code DynamicFormRenderer} consumes:
 * {@code {errors: {fieldKey: msg}, warnings: {fieldKey: msg}}}. Accepts either that shape,
 * or a list of {@code [{fieldKey, message, level}]}, or a bare {@code {fieldKey: msg}} map.
 */
function normaliseValidation(raw) {
  if (!raw || typeof raw !== 'object') return EMPTY_VALIDATION;
  if (raw.errors && typeof raw.errors === 'object' && !Array.isArray(raw.errors)) {
    return {
      errors: raw.errors || {},
      warnings: raw.warnings || {},
    };
  }
  if (Array.isArray(raw.fieldErrors)) {
    const errors = {};
    const warnings = {};
    for (const item of raw.fieldErrors) {
      if (!item || typeof item !== 'object') continue;
      const key = item.fieldKey || item.key;
      if (!key) continue;
      const bucket = item.level === 'warning' ? warnings : errors;
      bucket[key] = item.message || 'Invalid';
    }
    return { errors, warnings };
  }
  if (Array.isArray(raw)) {
    const errors = {};
    for (const item of raw) {
      if (!item || typeof item !== 'object') continue;
      const key = item.fieldKey || item.key;
      if (!key) continue;
      errors[key] = item.message || 'Invalid';
    }
    return { errors, warnings: {} };
  }
  // Bare map shape
  return { errors: raw, warnings: {} };
}

/**
 * Client-side state machine for the respondent-facing Form Flow shell.
 *
 * Lifecycle:
 *  1. On mount, calls {@code GET /form-flow/session/{fsid}}.
 *     - On 401/403 with {@code accessMode} in the body, flips to the matching gate state
 *       (DISABLED / access denied / email-required / auth-required) and stops. The shell
 *       renders the appropriate gate screen from {@code PublicAccessGateScreens}.
 *  2. On a successful load, the respondent edits answers locally; {@code saveDraft} debounces
 *     at 500ms.
 *  3. On submit, posts {@code /submit} and branches on {@code outcome}:
 *     - NEXT_STEP            → refetch session
 *     - BACK_STEP            → refetch session
 *     - CROSS_FLOW_NEXT_STEP → refetch session (URL unchanged)
 *     - CROSS_FLOW_HANDOFF   → window.location.assign(handoffUrl); surfaces an error if the
 *       server omits the URL rather than silently dropping into a refetch loop.
 *     - TRANSITIONING        → hold in transitioning state; SSE + 3s fallback eventually
 *       refetches.
 *     - COMPLETION           → session marks SUBMITTED terminal.
 *  4. If submit returns a 4xx with a validation payload, we surface per-field errors via
 *     {@link normaliseValidation}.
 *  5. On back, posts {@code /back} and refetches.
 *
 * Magic-link gate: {@code useMagicLinkVerification} is called unconditionally at the top of
 * the hook and is keyed by {@code gateComponentId}. When the server's 401 response tells us
 * the current step is gated by {@code EMAIL_MAGIC_LINK}, we set {@code gateComponentId} and
 * the shell renders {@code EmailMagicLinkGateScreen}; the user requests a link via
 * {@link requestFlowMagicLink}, clicks it, lands back on the page with a {@code verifyToken}
 * query param, the hook exchanges it for a grant token, rebuilds the API client with that
 * grant token, and retries {@code /session}.
 */
export default function useFormFlow({ formSessionId, test = false }) {
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // idle | submitting | transitioning | completed | error
  const [submitState, setSubmitState] = useState('idle');
  const [completion, setCompletion] = useState(null);
  const [sseError, setSseError] = useState(false);
  const [validation, setValidation] = useState(EMPTY_VALIDATION);

  // Gate state captured from 401/403 responses. See FlowAuthRequiredException /
  // FlowAccessDeniedException on the server side for the body shape.
  const [gateState, setGateState] = useState(null);
  const [gateComponentId, setGateComponentId] = useState(null);

  // useMagicLinkVerification must be called unconditionally; passing an empty resourceId
  // makes its effect a no-op until we actually know which componentId the grant token
  // should be scoped to.
  const {
    grantToken,
    verificationError,
    clearGrantToken,
  } = useMagicLinkVerification(gateComponentId || '', verifyFlowMagicLink);

  const api = useMemo(
    () => createFormFlowApi(formSessionId, { test, grantToken: grantToken || null }),
    [formSessionId, test, grantToken],
  );

  const draftTimerRef = useRef(null);
  const fallbackTimerRef = useRef(null);
  const answersRef = useRef({});

  useEffect(() => { answersRef.current = answers; }, [answers]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const s = await api.getSession();
      setSession(s);
      setGateState(null);
      setGateComponentId(null);
      const saved = s?.currentStep?.savedDraft?.answers;
      setAnswers(saved ? { ...saved } : {});
      setValidation(EMPTY_VALIDATION);
      if (s?.status === 'SUBMITTED') {
        setSubmitState('completed');
      } else if (s?.status === 'TRANSITIONING') {
        setSubmitState('transitioning');
      } else {
        setSubmitState('idle');
      }
      setSseError(false);
    } catch (e) {
      const status = e?.response?.status;
      const body = e?.response?.data || {};
      const nextGate = deriveGateState(status, body.accessMode);
      if (nextGate) {
        setGateState(nextGate);
        setGateComponentId(body.componentId || null);
        setError(null);
        setSession(null);
      } else {
        setError(e);
      }
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  // Surface verification failures (e.g. expired or revoked magic link) to the user as a
  // gate-screen error. Keeps the email gate visible so they can request a fresh link.
  useEffect(() => {
    if (verificationError) {
      setGateState('email_required');
      setError(null);
    }
  }, [verificationError]);

  const onChangeAnswer = useCallback((key, value) => {
    setAnswers((prev) => {
      const next = { ...prev, [key]: value };
      answersRef.current = next;
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
      const componentId = session?.currentStep?.componentId;
      if (componentId) {
        draftTimerRef.current = setTimeout(() => {
          // Drafts are best-effort; a 401 during draft save means the grant expired — we'll
          // discover that on the next read/submit and re-gate then.
          api.saveDraft(componentId, next).catch(() => {});
        }, DRAFT_DEBOUNCE_MS);
      }
      return next;
    });
    // Optimistic clear of the per-field error as soon as the user edits. Prevents stale
    // highlights after the respondent has already fixed the offending input.
    setValidation((prev) => {
      if (!prev?.errors || !(key in prev.errors)) return prev;
      const nextErrors = { ...prev.errors };
      delete nextErrors[key];
      return { errors: nextErrors, warnings: prev.warnings || {} };
    });
  }, [api, session]);

  const onFieldBlur = useCallback(() => {
    // Reserved for future server-side per-field validation; no-op today but threaded
    // through so the renderer's onBlur callback site has a safe target.
  }, []);

  const scheduleFallback = useCallback(() => {
    if (fallbackTimerRef.current) clearTimeout(fallbackTimerRef.current);
    fallbackTimerRef.current = setTimeout(() => {
      fallbackTimerRef.current = null;
      load();
    }, SSE_FALLBACK_MS);
  }, [load]);

  const clearFallback = useCallback(() => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearFallback(), [clearFallback]);

  const submit = useCallback(async () => {
    const componentId = session?.currentStep?.componentId;
    if (!componentId) return;
    setSubmitState('submitting');
    setError(null);
    setValidation(EMPTY_VALIDATION);
    try {
      const result = await api.submit(componentId, answersRef.current);
      if (!result || !result.outcome) {
        throw new Error('Unexpected submit response — no outcome.');
      }
      if (result.outcome === 'CROSS_FLOW_HANDOFF') {
        if (!result.handoffUrl) {
          throw new Error('Server returned CROSS_FLOW_HANDOFF without a handoffUrl.');
        }
        window.location.assign(result.handoffUrl);
        return;
      }
      if (result.outcome === 'COMPLETION') {
        // The FormFlowShell success card is gated on
        // {@code session.status === 'SUBMITTED'} (via PublicFormShell), not on
        // submitState. Without a refetch here, the shell keeps rendering the
        // editable form with Submit enabled, which produces a "submit does
        // nothing → respondent clicks again" loop — and historically a
        // follow-up 404 "No FlowSession for formSessionId=..." before the
        // engine's idempotent path landed. Refetching flips the persisted
        // SUBMITTED status onto {@code session.status}, which paints the
        // end state and disables Submit in one step.
        //
        // Preserve the rich completionPayload from the FIRST COMPLETION
        // response — idempotent replays from the backend return
        // {@code completionPayload: null} (see HandoffResultMapper
        // buildIdempotentResult), and we must not overwrite an authored
        // end-state with the generic fallback if a stray double-click
        // sneaks in before the refetch lands.
        if (result.completionPayload) {
          setCompletion(result.completionPayload);
        } else {
          setCompletion((prev) => prev || { message: 'Thanks!' });
        }
        setSubmitState('completed');
        await load();
        return;
      }
      if (result.outcome === 'TRANSITIONING') {
        setSubmitState('transitioning');
        setSseError(false);
        scheduleFallback();
        return;
      }
      // NEXT_STEP | BACK_STEP | CROSS_FLOW_NEXT_STEP → refetch
      await load();
    } catch (e) {
      const status = e?.response?.status;
      const body = e?.response?.data || {};
      // 4xx validation failures: surface per-field errors without flipping into the
      // error terminal state — the respondent just needs to fix their answers and retry.
      if (status === 400 && (body.errors || body.fieldErrors)) {
        setValidation(normaliseValidation(body));
        setSubmitState('idle');
        return;
      }
      // 401/403 after submit means the grant / auth expired mid-session. Re-gate.
      const nextGate = deriveGateState(status, body.accessMode);
      if (nextGate) {
        setGateState(nextGate);
        setGateComponentId(body.componentId || null);
        if (nextGate !== 'email_required') clearGrantToken();
        setSubmitState('idle');
        return;
      }
      setError(e);
      setSubmitState('error');
    }
  }, [api, session, load, scheduleFallback, clearGrantToken]);

  const back = useCallback(async (targetComponentId) => {
    setSubmitState('submitting');
    setError(null);
    try {
      await api.back(targetComponentId);
      await load();
    } catch (e) {
      const status = e?.response?.status;
      const body = e?.response?.data || {};
      const nextGate = deriveGateState(status, body.accessMode);
      if (nextGate) {
        setGateState(nextGate);
        setGateComponentId(body.componentId || null);
        if (nextGate !== 'email_required') clearGrantToken();
        setSubmitState('idle');
        return;
      }
      setError(e);
      setSubmitState('error');
    }
  }, [api, load, clearGrantToken]);

  const refresh = useCallback(() => load(), [load]);

  const onStreamEvent = useCallback((eventName /* parsed */) => {
    if (eventName === 'step_ready') {
      clearFallback();
      setSseError(false);
      load();
      return;
    }
    if (eventName === 'error') {
      setSseError(true);
      return;
    }
    // heartbeat → no-op; liveness only.
  }, [clearFallback, load]);

  const retryTransitioning = useCallback(() => {
    setSseError(false);
    scheduleFallback();
    load();
  }, [scheduleFallback, load]);

  /**
   * Request a magic link for the current gated step. The server scopes the resulting grant
   * token to {@code gateComponentId}, which matches what {@code FormFlowController}'s
   * {@code enforceAccess} checks against on the retry.
   */
  const requestMagicLink = useCallback(async (email) => {
    if (!gateComponentId) {
      throw new Error('No step componentId available to scope the magic link to.');
    }
    await requestFlowMagicLink(gateComponentId, email);
  }, [gateComponentId]);

  /**
   * Sign-in complete handler passed to {@code AuthGateScreen}. Retries the session load,
   * which will now include the respondent's JWT via {@code authHeaders}.
   */
  const onSignedIn = useCallback(() => {
    setGateState(null);
    load();
  }, [load]);

  return {
    session,
    answers,
    loading,
    error,
    submitState,
    completion,
    sseError,
    validation,
    gateState,
    gateComponentId,
    streamUrl: api.streamUrl(),
    onStreamEvent,
    retryTransitioning,
    onChangeAnswer,
    onFieldBlur,
    submit,
    back,
    refresh,
    requestMagicLink,
    onSignedIn,
  };
}
